import { listCallHistory } from '../../../services/call-history-service'
import { listDirectoryUsers } from '../../../services/chat-service'
import { resolveAvatarDisplayUrl } from '../../../services/profile-service'
import { replaceNativeCarRecents } from '../../../services/voice-call/native-call'
import { isNativeApp } from '../../../utils/platform'
import type { CallHistoryRecord } from '../../../types/voice-call'

/** Mirror at most this many history peers into the car History store. */
export const CAR_RECENTS_SYNC_LIMIT = 20

export type CarRecentEntry = {
  peerId: string
  peerName: string
  ts: number
  avatarUrl?: string | null
}

type DirectoryName = { displayName?: string | null; avatarUrl?: string | null }

/**
 * Build the car-History list from call history: 1:1 calls only, deduped by
 * peer keeping the MOST RECENT call, newest first. Peers with no resolvable
 * display name are skipped (the car UI must never show a raw user id).
 * `ts` is epoch milliseconds from `startedAt`.
 */
export function buildCarRecentsFromHistory(
  records: CallHistoryRecord[],
  lookup: (userId: string) => DirectoryName | null,
  limit = CAR_RECENTS_SYNC_LIMIT,
): CarRecentEntry[] {
  const seen = new Set<string>()
  const out: CarRecentEntry[] = []
  // listCallHistory returns newest-first; keep that order.
  for (const record of records) {
    if (out.length >= limit) break
    if (record.isGroup) continue
    const peerId = record.peerId
    if (!peerId || seen.has(peerId)) continue
    seen.add(peerId)
    const info = lookup(peerId)
    const peerName = (info?.displayName ?? '').trim()
    if (!peerName) continue
    const ts = Date.parse(record.startedAt)
    const avatarUrl = (info?.avatarUrl ?? '').trim()
    const row: CarRecentEntry = {
      peerId,
      peerName,
      ts: Number.isFinite(ts) ? ts : 0,
    }
    if (avatarUrl) row.avatarUrl = avatarUrl
    out.push(row)
  }
  return out
}

/**
 * Push the user's real CALL HISTORY into the native CarPlay / Android Auto
 * History store, so the car screen shows the same recents as the app's Calls
 * page — not just calls placed since this binary was installed.
 *
 * Native `replace()` writes the list as-is (newest first). Fire-and-forget:
 * the car screen degrades to its incremental per-call writes if this never runs.
 */
export async function syncCarRecentsFromHistory(): Promise<void> {
  if (!isNativeApp()) return
  try {
    const [records, users] = await Promise.all([
      listCallHistory(CAR_RECENTS_SYNC_LIMIT * 3),
      listDirectoryUsers(),
    ])
    if (!records.length) return
    const byId = new Map(users.map((u) => [u.userId, u]))
    const recents = buildCarRecentsFromHistory(records, (id) => {
      const u = byId.get(id)
      return u ? { displayName: u.displayName, avatarUrl: u.avatarUrl } : null
    })
    const withDisplayAvatars = await Promise.all(
      recents.map(async (r) => {
        if (!r.avatarUrl) return r
        const display = await resolveAvatarDisplayUrl(r.avatarUrl)
        return display ? { ...r, avatarUrl: display } : { peerId: r.peerId, peerName: r.peerName, ts: r.ts }
      }),
    )
    await replaceNativeCarRecents(withDisplayAvatars)
  } catch {
    /* best-effort — incremental per-call writes still populate the store */
  }
}
